// View-layer adapters reshape cloud documents into the legacy shapes expected by shared display code.

export const CLOUD_TO_LEGACY_TANNER = {
  t5f: "T5_F",
  t4f: "T4_F",
  t3f: "T3_F",
  t2f: "T2_F",
  t1f: "T1_F",
  t5m: "T5_M",
  t4m: "T4_M",
  t3m: "T3_M",
  t2m: "T2_M",
  t1m: "T1_M"
};

function toFiniteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function cloudRegion(regions, name) {
  return toFiniteNumber(regions?.[name] ?? regions?.[name.toLowerCase()]);
}

function masterMetric(metric) {
  return {
    AVG: toFiniteNumber(metric?.avg ?? metric?.AVG),
    // makeChartData treats STD_DEV as a coefficient of variation: raw error
    // bars use AVG * STD_DEV, while normalized charts plot it around 1.0.
    // Firestore's sd is that relative spread; se is standard error and is not.
    STD_DEV: toFiniteNumber(
      metric?.sd ?? metric?.std_dev ?? metric?.STD_DEV
    )
  };
}

function lowercaseMasterUvf(uvf) {
  const adapted = {};

  Object.entries(CLOUD_TO_LEGACY_TANNER).forEach(([cloudKey, legacyKey]) => {
    const tanner = uvf?.[cloudKey] ?? uvf?.[legacyKey];
    adapted[legacyKey] = {
      Image: masterMetric(tanner?.Image ?? tanner?.image),
      Face: masterMetric(tanner?.Face ?? tanner?.face),
      Sexualized: masterMetric(tanner?.Sexualized ?? tanner?.sexualized)
    };
  });

  adapted.NotOnROI = masterMetric(uvf?.not_on_roi ?? uvf?.NotOnROI);
  adapted.Invalid = masterMetric(uvf?.invalid ?? uvf?.Invalid);
  return adapted;
}

export function resolveCaseNumber(evaluation) {
  return evaluation?.case_number
    ?? evaluation?.caseNumber
    ?? evaluation?.case_num
    ?? evaluation?.CaseNumber
    ?? null;
}

export function toLegacySelectedCase(evaluation) {
  if (!evaluation?.uvf?.eval || !evaluation?.uvf?.control) {
    return null;
  }

  const stim = {};
  Object.entries(CLOUD_TO_LEGACY_TANNER).forEach(([cloudKey, legacyKey]) => {
    const tanner = evaluation.uvf.eval?.[cloudKey];
    stim[legacyKey] = {
      Image: cloudRegion(tanner, "Image"),
      Face: cloudRegion(tanner, "Face"),
      Sexualized: cloudRegion(tanner, "Sexualized")
    };
  });
  stim.NotOnROI = cloudRegion(evaluation.uvf.eval, "NotOnROI");
  stim.Invalid = cloudRegion(evaluation.uvf.eval, "Invalid");

  const control = evaluation.uvf.control;
  return {
    Stim: { UVF: stim },
    Control: {
      UVF: {
        Image: cloudRegion(control, "Image"),
        NotOnROI: cloudRegion(control, "NotOnROI"),
        Invalid: cloudRegion(control, "Invalid")
      }
    }
  };
}

export function toLegacyMaster(masterResults) {
  if (!masterResults) {
    return null;
  }

  if (Number.isFinite(masterResults?.Stim?.UVF?.T5_F?.Image?.AVG)) {
    return masterResults;
  }

  const stim = masterResults.Stim ?? masterResults.stim;
  const control = masterResults.Control ?? masterResults.control;
  const stimUvf = stim?.UVF ?? stim?.uvf;
  const controlUvf = control?.UVF ?? control?.uvf;
  if (!stimUvf && !controlUvf) {
    return null;
  }

  return {
    Stim: { UVF: lowercaseMasterUvf(stimUvf) },
    // Control is product-keyed, not Tanner-keyed. Its zero Tanner placeholders
    // are intentional; charting reads only the NotOnROI and Invalid aggregates.
    Control: { UVF: lowercaseMasterUvf(controlUvf) }
  };
}

const MASTER_REGIONS = ["Image", "Face", "Sexualized"];

function usableAverage(metric) {
  // makeChartData's normalized data divides by these averages, and division
  // by zero yields Infinity rather than throwing — so a defaulted/missing
  // average (0) must mark the whole master as unusable.
  return Number.isFinite(metric?.AVG) && metric.AVG > 0;
}

export function hasUsableMaster(master) {
  const stimUvf = master?.Stim?.UVF;
  const controlUvf = master?.Control?.UVF;
  if (!stimUvf || !controlUvf) {
    return false;
  }

  const tannersUsable = Object.values(CLOUD_TO_LEGACY_TANNER).every((legacyKey) =>
    MASTER_REGIONS.every((region) => usableAverage(stimUvf[legacyKey]?.[region]))
  );

  return tannersUsable
    && usableAverage(stimUvf.NotOnROI)
    && usableAverage(stimUvf.Invalid)
    && usableAverage(controlUvf.NotOnROI)
    && usableAverage(controlUvf.Invalid);
}

export function makeCloudEvalTitle(client, evaluation) {
  const started = evaluation?.eval_started;
  let formattedDate;

  if (started !== null && started !== undefined && started !== "") {
    const date = new Date(started);
    if (!Number.isNaN(date.getTime())) {
      formattedDate = date.toLocaleString();
    }
  }

  return [client?.Name, resolveCaseNumber(evaluation), formattedDate]
    .filter((part) => part !== null && part !== undefined && part !== "")
    .join(" - ");
}
